import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import RoomBreakdownDetails from './RoomBreakdownDetails'
import RoomBreakdownLoadingSkeleton from './RoomBreakdownLoadingSkeleton'
import useOfferMetaData from '../../../../hooks/Offers/useOfferMetaData'
import { isBedbankSearchRoom, isLEOffer, isReservationSearchRoom } from 'lib/offer/offerTypes'
import { shouldHideSearchTileRoomBreakdown } from './searchTileRoomBreakdownUtils'
import useBedbankRoomBreakdown from '../../../../hooks/useBedbankRoomBreakdown'
import { getRoomBreakdown, mapSearchOccupancyToOccupants } from 'lib/search/searchUtils'
import { excludeNullOrUndefined } from 'checkout/utils'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
}

function SearchTileRoomBreakdown({
  offer,
  filters,
}: Props) {
  const intl = useIntl()
  const metaData = useOfferMetaData(offer.id, filters)

  const recommendedForText = useMemo(() => {
    const numberOfAdults = filters?.rooms?.reduce((sum, room) => sum + room.adults, 0) ?? 0
    const numberOfChildren = filters?.rooms?.reduce((sum, room) => sum + (room.teenagers ?? 0) + (room.children ?? 0) + (room.infants ?? 0), 0) ?? 0
    // we don't want to concatenate the strings here cuz it breaks RTL languages but can't figure another way of doing this without separating 2 clauses of the sentence
    return intl.formatMessage({
      id: 'hFKgypXkRdFZeyMIfhxs',
      defaultMessage: 'Recommended for {numberOfAdults, plural, one {# adult} other {# adults}}{numberOfChildren, plural, =0 {} one {, # child} other {, # children}}',
      description: 'Room breakdown recommendation showing adult and optional children guest count on search tile',
    }, { numberOfAdults, numberOfChildren })
  }, [filters?.rooms, intl])

  const leRoomsList = useMemo(() => {
    const packages = isLEOffer(offer) ? offer.packages : []
    const metaDataRooms = metaData?.rooms?.filter(isReservationSearchRoom) ?? []
    return getRoomBreakdown(metaDataRooms, packages)
  }, [metaData?.rooms, offer])

  const bedbankRooms = useMemo(() =>
    metaData?.rooms
      ?.filter(isBedbankSearchRoom)
      .map(r => (r.rateId ?
          {
            rateId: r.rateId,
            propertyId: r.propertyId,
            occupancy: mapSearchOccupancyToOccupants(r.occupancy),
          }
        : undefined))
      .filter(excludeNullOrUndefined) ?? [],
  [metaData?.rooms])

  const { rooms: bedbankRoomsList, isLoading: isBedbankLoading } = useBedbankRoomBreakdown(bedbankRooms, filters)

  const shouldHideRoomBreakdown = useMemo(() =>
    shouldHideSearchTileRoomBreakdown({ offer, filters }),
  [offer, filters])

  const allRooms = useMemo(() =>
    [...leRoomsList, ...bedbankRoomsList],
  [leRoomsList, bedbankRoomsList])

  if (shouldHideRoomBreakdown) return null

  if (isBedbankLoading) return <RoomBreakdownLoadingSkeleton />

  if (allRooms.length === 0) return null

  return (
    <RoomBreakdownDetails
      recommendedForText={recommendedForText}
      rooms={allRooms}
    />
  )
}

export default SearchTileRoomBreakdown
